import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { get } from '../../helpers/api_helper';
import PaceSparkline from './PaceSparkline';

const ORDER_FLOW_CHART_THRESHOLDS = { warningPoints: 0, criticalPoints: 6 };
const ORDER_FLOW_BENCHMARKS = {
  11: 50,
  12: 60,
  13: 70,
  14: 80,
  15: 90,
};

function orderFlowChartTimeline(timeline = [], thresholds = ORDER_FLOW_CHART_THRESHOLDS) {
  let target = 0;

  return timeline.map((point) => {
    const scheduledTarget = ORDER_FLOW_BENCHMARKS[point.hour];
    if (scheduledTarget) target = scheduledTarget;

    let status = null;
    if (Number.isFinite(point.current)) {
      if (target === 0) {
        status = 'pending';
      } else {
        const shortfall = target - point.current;
        if (shortfall <= thresholds.warningPoints) status = 'ahead';
        else if (shortfall <= thresholds.criticalPoints) status = 'neutral';
        else status = 'behind';
      }
    }

    return {
      ...point,
      average: target,
      bar_target: target || null,
      reference_label: scheduledTarget ? `${scheduledTarget}%` : null,
      status,
    };
  });
}

function paceLabel(comparison, t) {
  if (comparison.status === 'ahead' && Number.isFinite(comparison.orders_to_next_target)) {
    return (
      <>
        <strong className="today-awaiting-card__pace-number">
          {comparison.orders_to_next_target}
        </strong>
        {' '}
        {t('TO NEXT TARGET')}
      </>
    );
  }
  if (comparison.status === 'ahead') {
    return t('On target');
  }

  return (
    <>
      <strong className="today-awaiting-card__pace-number">{comparison.orders_needed}</strong>
      {' '}
      {t('TO TARGET')}
    </>
  );
}

function AwaitingOrdersWidget({ refreshKey, t }) {
  const [count, setCount] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadCount() {
      try {
        const response = await get('/api/today/awaiting-orders');
        if (mounted) {
          setCount(Number(response.count || 0));
          setComparison(response.comparison || null);
          setError(false);
        }
      } catch (requestError) {
        if (mounted) setError(true);
      }
    }

    loadCount();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const countStatus = error ? 'error' : (comparison?.status || 'neutral');
  const chartThresholds = {
    ...ORDER_FLOW_CHART_THRESHOLDS,
    criticalPoints: comparison?.tolerance ?? ORDER_FLOW_CHART_THRESHOLDS.criticalPoints,
  };
  const chartTimeline = orderFlowChartTimeline(comparison?.timeline, chartThresholds);

  return (
    <div className="today-awaiting-card">
      <div className="today-awaiting-card__heading">
        <span>{t('Order queue')}</span>
        <i className="mdi mdi-truck-fast-outline" aria-hidden="true" />
      </div>
      <div className={`today-awaiting-card__count today-awaiting-card__count--${countStatus}`}>
        {error ? '—' : (count ?? '…')}
      </div>
      <h2>{t('Orders Awaiting')}</h2>
      <p className={`today-awaiting-card__pace today-awaiting-card__pace--${countStatus}`}>
        {comparison && !error
          ? paceLabel(comparison, t)
          : t('Not shipped · Not voided')}
      </p>
      {chartTimeline.length > 0 && !error && (
        <PaceSparkline
          label={t('Order flow')}
          referenceLabel={t('Benchmark')}
          referenceStartHour={11}
          showAllHourLabels
          thresholds={chartThresholds}
          timeline={chartTimeline}
          todayLabel={t('Hour mark · cur {{progress}}%', { progress: comparison.progress })}
          unit="progress"
        />
      )}
    </div>
  );
}

AwaitingOrdersWidget.defaultProps = {
  t: (value) => value,
};

AwaitingOrdersWidget.propTypes = {
  refreshKey: PropTypes.number.isRequired,
  t: PropTypes.func,
};

export default withTranslation()(AwaitingOrdersWidget);
